import type { AnswerKey, MultiScope, NodeId, QuestionId, TemplateNodeId } from './types';

/**
 * Sleutels voor antwoorden en runtime-knopen. Het formaat ligt vast omdat de
 * PHP-engine dezelfde sleutels moet produceren; wijzig het niet zonder de
 * API-kant mee te nemen.
 */

/** Het bereik buiten elke multi-container. */
export const ROOT_SCOPE: MultiScope = '';

/** Scheidingstekens; los van elkaar gekozen zodat een sleutel eenduidig terug te lezen is. */
const SCOPE_SEPARATOR = '@';
const NODE_SEPARATOR = '#';
const SCOPE_NESTING = '/';
const MULTI_COUNT_PREFIX = 'm';

/**
 * Het bereik van de instanties van een container: `"178.0"`, genest
 * `"156.0/178.1"`. De index is 0-based, net als het legacy `submultiid`.
 */
export function childScope(
    parentScope: MultiScope,
    containerNodeId: TemplateNodeId,
    index: number,
): MultiScope {
    const segment = `${containerNodeId}.${index}`;

    return parentScope === ROOT_SCOPE ? segment : `${parentScope}${SCOPE_NESTING}${segment}`;
}

/** De identiteit van een knoop in de runtime-boom. */
export function runtimeNodeId(nodeId: TemplateNodeId, scope: MultiScope): NodeId {
    return scope === ROOT_SCOPE ? String(nodeId) : `${nodeId}${NODE_SEPARATOR}${scope}`;
}

/** De sleutel waaronder het antwoord op een vraag binnen dit bereik staat. */
export function questionKey(questionId: QuestionId, scope: MultiScope = ROOT_SCOPE): AnswerKey {
    return scope === ROOT_SCOPE ? String(questionId) : `${questionId}${SCOPE_SEPARATOR}${scope}`;
}

/**
 * De sleutel van de synthetische aantalsvraag van een multi-container
 * ("Hoeveel balkons zijn er?"). Het antwoord staat in `value_number`.
 */
export function multiCountKey(containerNodeId: NodeId): AnswerKey {
    return `${MULTI_COUNT_PREFIX}${SCOPE_SEPARATOR}${containerNodeId}`;
}

/**
 * De sleutel van een vraag zoals hij in een knoop gesteld wordt.
 *
 * Een vraag met `ask_once` (het legacy `max1xvragen`) wordt hooguit één keer
 * gesteld en deelt zijn antwoord met élke knoop waarin hij voorkomt — ook over
 * multi-instanties heen. Zo'n vraag houdt dus altijd zijn wortelsleutel.
 * Geverifieerd: zonder deze regel wijken 10 van de 455 knopen af.
 */
export function answerKeyFor(
    questionId: QuestionId,
    scope: MultiScope,
    askOnce: boolean,
): AnswerKey {
    return questionKey(questionId, askOnce ? ROOT_SCOPE : scope);
}

/** Ontleedt een vraagsleutel terug naar vraag-id en bereik. */
export function parseQuestionKey(key: AnswerKey): { questionId: QuestionId; scope: MultiScope } {
    const index = key.indexOf(SCOPE_SEPARATOR);

    if (index < 0) {
        return { questionId: Number(key), scope: ROOT_SCOPE };
    }

    return { questionId: Number(key.slice(0, index)), scope: key.slice(index + 1) };
}

/** `true` voor de sleutel van een multi-aantalsvraag. */
export function isMultiCountKey(key: AnswerKey): boolean {
    return key.startsWith(`${MULTI_COUNT_PREFIX}${SCOPE_SEPARATOR}`);
}
